// ═══════════════════════════════════════════════════════════════════
//  DAILY-LIMIT OVERRIDE (AB#606)
//
//  A manager (`appointments:manage`) booking a host past their daily limit
//  of appointments, on purpose and with a reason. Offered on the staff
//  booking and the reassignment, and only after the server has said the
//  host is capped that day: 409 with code "dailyLimitReached". The server
//  decides that from its own count — Cal shows a capped day only as one
//  with no availability — so the grid's "Daily limit reached" label and
//  this refusal come from the same place.
//
//  Shares the request's `overrideReason` with the double-booking override:
//  one reason explains whatever the manager chose to push past.
// ═══════════════════════════════════════════════════════════════════

/** The 409 code for "the host has reached their daily limit" — DailyLimitOverrideRequest.DailyLimitReachedCode. */
export const DAILY_LIMIT_REACHED_CODE = "dailyLimitReached"

/** Whether a failed request is the server saying the host is at their daily limit — the refusal the override is for. */
export function isDailyLimitRefusal(error: unknown): boolean {
  const failure = error as { status?: number | string; data?: unknown } | undefined
  const code = (failure?.data as { code?: unknown } | undefined)?.code
  return failure?.status === 409 && code === DAILY_LIMIT_REACHED_CODE
}

/** The override as the request bodies take it, or nothing when it is not being used. */
export function dailyLimitFields(armed: boolean, reason: string): { overrideDailyLimit?: true; overrideReason?: string } {
  return armed ? { overrideDailyLimit: true, overrideReason: reason.trim() } : {}
}

/** The override's fields as the booking read model carries them (`BookingStatusView`). */
export interface DailyLimitOverrideView {
  dailyLimitOverride?: boolean
  dailyLimitOverrideBy?: string | null
  dailyLimitOverrideReason?: string | null
}

/**
 * The tooltip on a booking's "Past daily limit (override)" badge: who, and why. Undefined for a
 * booking that is not one.
 */
export function describeDailyLimitOverride(view: DailyLimitOverrideView): string | undefined {
  if (!view.dailyLimitOverride) return undefined

  const who = view.dailyLimitOverrideBy ? ` by ${view.dailyLimitOverrideBy}` : ""
  const why = view.dailyLimitOverrideReason?.trim()
  return `Booked past the host's daily limit on purpose${who}.${why ? ` Reason: ${why}` : ""}`
}
